import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileUp, X } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import * as z from "zod";

import { Button, buttonVariants } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

import useFileUpload from "@/hooks/useFileUpload";
import { cn } from "@/lib/utils";
import {
  ENCOUNTER_ADMIT_SOURCE,
  ENCOUNTER_ARRIVAL_MODE,
  ENCOUNTER_DIET_PREFERENCE,
  EncounterAdmitSources,
  EncounterArrivalMode,
  EncounterDietPreference,
  EncounterRead,
} from "@/types/emr/encounter/encounter";
import encounterApi from "@/types/emr/encounter/encounterApi";
import {
  BACKEND_ALLOWED_EXTENSIONS,
  FileCategory,
  FileType,
} from "@/types/files/file";
import mutate from "@/Utils/request/mutate";

interface Props {
  encounter: EncounterRead;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (encounter: EncounterRead) => void;
}

const toDateTimeLocal = (value?: string | null) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
};

const toISOString = (value: string) =>
  value ? new Date(value).toISOString() : undefined;

export function HospitalizationDetailsDialog({
  encounter,
  open,
  onOpenChange,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const schema = z
    .object({
      arrival_mode: z.enum(ENCOUNTER_ARRIVAL_MODE, {
        required_error: t("arrival_mode_required"),
      }),
      arrival_time: z.string().min(1, "Arrival time is required"),
      admission_time: z.string().min(1, "Admission time is required"),
      expected_discharge_date: z.string().optional(),
      re_admission: z.boolean(),
      medico_legal_case: z.boolean(),
      admit_source: z.enum(ENCOUNTER_ADMIT_SOURCE).optional(),
      diet_preference: z.enum(ENCOUNTER_DIET_PREFERENCE).optional(),
      referred_by_practitioner: z.string(),
      referred_by_facility: z.string(),
    })
    .superRefine((data, context) => {
      if (data.arrival_mode !== "referral") return;

      if (!data.referred_by_practitioner.trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["referred_by_practitioner"],
          message: t("referred_by_practitioner_required"),
        });
      }

      if (!data.referred_by_facility.trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["referred_by_facility"],
          message: t("referred_by_facility_required"),
        });
      }
    });

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      arrival_mode: encounter.hospitalization?.arrival_mode,
      arrival_time: toDateTimeLocal(encounter.hospitalization?.arrival_time),
      admission_time: toDateTimeLocal(
        encounter.hospitalization?.admission_time ?? encounter.period.start,
      ),
      expected_discharge_date:
        encounter.hospitalization?.expected_discharge_date ?? "",
      re_admission: encounter.hospitalization?.re_admission ?? false,
      medico_legal_case: encounter.hospitalization?.medico_legal_case ?? false,
      admit_source: encounter.hospitalization?.admit_source,
      diet_preference: encounter.hospitalization?.diet_preference,
      referred_by_practitioner:
        encounter.hospitalization?.referred_by_practitioner ?? "",
      referred_by_facility:
        encounter.hospitalization?.referred_by_facility ?? "",
    },
  });

  const arrivalMode = form.watch("arrival_mode");
  const fileUpload = useFileUpload({
    type: FileType.ENCOUNTER,
    category: FileCategory.REFERRAL_LETTER,
    multiple: false,
    allowedExtensions: BACKEND_ALLOWED_EXTENSIONS,
  });

  useEffect(() => {
    if (!open) return;

    form.reset({
      arrival_mode: encounter.hospitalization?.arrival_mode,
      arrival_time: toDateTimeLocal(encounter.hospitalization?.arrival_time),
      admission_time: toDateTimeLocal(
        encounter.hospitalization?.admission_time ?? encounter.period.start,
      ),
      expected_discharge_date:
        encounter.hospitalization?.expected_discharge_date ?? "",
      re_admission: encounter.hospitalization?.re_admission ?? false,
      medico_legal_case: encounter.hospitalization?.medico_legal_case ?? false,
      admit_source: encounter.hospitalization?.admit_source,
      diet_preference: encounter.hospitalization?.diet_preference,
      referred_by_practitioner:
        encounter.hospitalization?.referred_by_practitioner ?? "",
      referred_by_facility:
        encounter.hospitalization?.referred_by_facility ?? "",
    });
  }, [
    encounter.hospitalization?.arrival_mode,
    encounter.hospitalization?.arrival_time,
    encounter.hospitalization?.admission_time,
    encounter.hospitalization?.expected_discharge_date,
    encounter.hospitalization?.re_admission,
    encounter.hospitalization?.medico_legal_case,
    encounter.hospitalization?.admit_source,
    encounter.hospitalization?.diet_preference,
    encounter.period.start,
    encounter.hospitalization?.referred_by_facility,
    encounter.hospitalization?.referred_by_practitioner,
    form,
    open,
  ]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) fileUpload.clearFiles();
    onOpenChange(nextOpen);
  };

  const { mutate: saveDetails, isPending } = useMutation({
    mutationFn: async (data: z.infer<typeof schema>) => {
      const updatedEncounter = await mutate(encounterApi.update, {
        pathParams: { id: encounter.id },
      })({
        status: encounter.status,
        encounter_class: encounter.encounter_class,
        period: encounter.period,
        priority: encounter.priority,
        external_identifier: encounter.external_identifier,
        discharge_summary_advice: encounter.discharge_summary_advice,
        hospitalization: {
          ...encounter.hospitalization,
          arrival_mode: data.arrival_mode,
          arrival_time: toISOString(data.arrival_time),
          admission_time: toISOString(data.admission_time),
          expected_discharge_date: data.expected_discharge_date || undefined,
          re_admission: data.re_admission,
          medico_legal_case: data.medico_legal_case,
          admit_source: data.admit_source,
          diet_preference: data.diet_preference,
          referred_by_practitioner:
            data.arrival_mode === "referral"
              ? data.referred_by_practitioner.trim()
              : undefined,
          referred_by_facility:
            data.arrival_mode === "referral"
              ? data.referred_by_facility.trim()
              : undefined,
        },
      });

      if (fileUpload.files.length > 0) {
        await fileUpload.handleFileUpload(encounter.id);
      }

      return updatedEncounter;
    },
    onSuccess: (updatedEncounter) => {
      // Mutation responses omit the detail endpoint's permission projection.
      // Keep cached permissions until a full detail refetch replaces the record.
      queryClient.invalidateQueries({ queryKey: ["encounter", encounter.id] });
      queryClient.invalidateQueries({
        queryKey: ["files", FileType.ENCOUNTER, encounter.id],
      });
      toast.success(t("hospitalization_details_updated"));
      handleOpenChange(false);
      onSaved?.(updatedEncounter);
    },
  });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("hospitalization_details")}</DialogTitle>
          <DialogDescription>
            {t("arrival_and_referral_description")}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            className="space-y-4"
            onSubmit={form.handleSubmit((data) => saveDetails(data))}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="arrival_mode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("mode_of_arrival")}
                      <span className="text-red-500"> *</span>
                    </FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={(value: EncounterArrivalMode) =>
                        field.onChange(value)
                      }
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("select_arrival_mode")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ENCOUNTER_ARRIVAL_MODE.map((mode) => (
                          <SelectItem key={mode} value={mode}>
                            {t(`encounter_arrival_mode__${mode}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="arrival_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Arrival time <span className="text-red-500"> *</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} type="datetime-local" required />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="admission_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Admission time <span className="text-red-500"> *</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} type="datetime-local" required />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="expected_discharge_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expected discharge date</FormLabel>
                    <FormControl>
                      <Input {...field} type="date" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="re_admission"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 pt-7">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="cursor-pointer">
                      {t("readmission")}
                    </FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="admit_source"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("admit_source")}</FormLabel>
                    <Select
                      value={field.value ?? ""}
                      onValueChange={(value: EncounterAdmitSources) =>
                        field.onChange(value)
                      }
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("select_admit_source")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ENCOUNTER_ADMIT_SOURCE.map((source) => (
                          <SelectItem key={source} value={source}>
                            {t(`encounter_admit_sources__${source}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="medico_legal_case"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 pt-7">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="cursor-pointer">
                      Medico-Legal Case
                    </FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="diet_preference"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("diet_preference")}</FormLabel>
                    <Select
                      value={field.value ?? ""}
                      onValueChange={(value: EncounterDietPreference) =>
                        field.onChange(value)
                      }
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t("select_diet_preference")}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ENCOUNTER_DIET_PREFERENCE.map((preference) => (
                          <SelectItem key={preference} value={preference}>
                            {t(`encounter_diet_preference__${preference}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {arrivalMode === "referral" && (
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="referred_by_practitioner"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("referred_by_practitioner")}
                        <span className="text-red-500"> *</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t("enter_practitioner_name")}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="referred_by_facility"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("referred_by_facility")}
                        <span className="text-red-500"> *</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t("enter_facility_name")}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <div className="space-y-2">
              <FormLabel>{t("referral_letter")}</FormLabel>
              {fileUpload.Input({})}
              {fileUpload.files.length === 0 ? (
                <label
                  htmlFor={`file_upload_${FileType.ENCOUNTER}`}
                  className={cn(
                    buttonVariants({ variant: "outline" }),
                    "w-full cursor-pointer justify-start font-normal",
                  )}
                >
                  <FileUp className="size-4" />
                  {t("choose_referral_letter")}
                </label>
              ) : (
                <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm">
                  <span className="truncate">{fileUpload.files[0]?.name}</span>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => fileUpload.removeFile(0)}
                  >
                    <X className="size-4" />
                    <span className="sr-only">{t("remove")}</span>
                  </Button>
                </div>
              )}
              {fileUpload.error && (
                <p className="text-sm text-red-600">{fileUpload.error}</p>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                {t("cancel")}
              </Button>
              <Button
                type="submit"
                disabled={isPending || fileUpload.uploading}
              >
                {isPending || fileUpload.uploading
                  ? t("saving")
                  : t("save_details")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
        {fileUpload.Dialogues}
      </DialogContent>
    </Dialog>
  );
}
